// Polygon and polyline clipping against a (possibly concave) polygon (CIN-15126, phase 1). Pure; relative imports only.
//
// DECISIONS
// - The plan named Greiner-Hormann with epsilon perturbation. This is the edge-splitting variant of the same
//   idea, chosen because it needs NO perturbation: both rings are cut at every crossing, touching and collinear
//   overlap, each piece is classified by its midpoint (inside / outside / on the other boundary), and the kept
//   pieces are linked into rings. Coincident edges (a tier band whose edge lies on a wall - the normal case)
//   are decided by direction: two edges that run the same way keep one copy, opposite ways cancel. That gives
//   exact results where a perturbing implementation leaves slivers.
// - Inputs may be given in either orientation; results are counter-clockwise. Rings are simple polygons without
//   a repeated last point; the intersection of two simply connected polygons has no holes, so none are returned.
// - Tolerances are absolute, in the caller's units (mm): points closer than `SNAP` are the same point.
// - Ring linking at a vertex that several kept pieces meet in (two pieces touching at a corner) takes the
//   sharpest left turn, so touching pieces stay separate rings instead of merging into a figure eight.

import type { PlanPoint } from './types.ts';

const EPS = 1e-6;
const SNAP = 1e-4;
const MIN_PIECE = 1e-4;

type Edge = { a: PlanPoint; b: PlanPoint };

const cross2 = (ax: number, ay: number, bx: number, by: number): number => ax * by - ay * bx;

function area(points: readonly PlanPoint[]): number {
    let twice = 0;
    for (let i = 0; i < points.length; i += 1) {
        const p = points[i];
        const q = points[(i + 1) % points.length];
        twice += p.x * q.y - q.x * p.y;
    }
    return twice / 2;
}

function counterClockwise(points: readonly PlanPoint[]): PlanPoint[] {
    const ring = points.map((p) => ({ x: p.x, y: p.y }));
    return area(ring) < 0 ? ring.reverse() : ring;
}

function edgesOfRing(ring: readonly PlanPoint[]): Edge[] {
    return ring.map((a, i) => ({ a, b: ring[(i + 1) % ring.length] }));
}

/** Distance from p to segment a-b. */
function distanceToSegment(p: PlanPoint, a: PlanPoint, b: PlanPoint): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length2 = dx * dx + dy * dy;
    const t = length2 < EPS ? 0 : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / length2));
    return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}

type Location = { where: 'in' | 'out' | 'on'; edge: Edge | null };

/** Where `p` is relative to the ring: inside, outside, or on an edge (which one). */
function locate(ring: readonly PlanPoint[], edges: readonly Edge[], p: PlanPoint): Location {
    let nearest: Edge | null = null;
    let nearestDistance = Infinity;
    for (const edge of edges) {
        const distance = distanceToSegment(p, edge.a, edge.b);
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = edge;
        }
    }
    if (nearest && nearestDistance <= SNAP * 2) return { where: 'on', edge: nearest };
    let inside = false;
    for (const { a, b } of edges) {
        if (a.y > p.y !== b.y > p.y && p.x < a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y)) inside = !inside;
    }
    return { where: inside ? 'in' : 'out', edge: null };
}

/**
 * Parameters (0..1, exclusive) along segment p-q at which segment r-s crosses, touches or overlaps it,
 * with the exact point to use, so both segments split at identical coordinates.
 */
function splitPoints(p: PlanPoint, q: PlanPoint, r: PlanPoint, s: PlanPoint): { t: number; point: PlanPoint }[] {
    const out: { t: number; point: PlanPoint }[] = [];
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const ex = s.x - r.x;
    const ey = s.y - r.y;
    const length = Math.hypot(dx, dy);
    if (length < MIN_PIECE) return out;
    const denom = cross2(dx, dy, ex, ey);
    const scale = length * Math.hypot(ex, ey);
    const add = (t: number, point: PlanPoint) => {
        if (t > SNAP / length && t < 1 - SNAP / length) out.push({ t, point });
    };
    if (Math.abs(denom) > 1e-9 * scale) {
        const t = cross2(r.x - p.x, r.y - p.y, ex, ey) / denom;
        const u = cross2(r.x - p.x, r.y - p.y, dx, dy) / denom;
        const slackT = SNAP / length;
        const slackU = SNAP / Math.hypot(ex, ey);
        if (t >= -slackT && t <= 1 + slackT && u >= -slackU && u <= 1 + slackU) {
            // Prefer the other segment's exact endpoint when the crossing lands on it.
            const point =
                u <= slackU
                    ? r
                    : u >= 1 - slackU
                      ? s
                      : { x: p.x + dx * Math.min(1, Math.max(0, t)), y: p.y + dy * Math.min(1, Math.max(0, t)) };
            add(t, point);
        }
        return out;
    }
    // Parallel: only a collinear overlap splits, at the other segment's endpoints.
    for (const end of [r, s]) {
        if (distanceToSegment(end, p, q) <= SNAP) {
            add(((end.x - p.x) * dx + (end.y - p.y) * dy) / (length * length), end);
        }
    }
    return out;
}

/** The ring's edges cut at every point where the other ring touches them. */
function splitRing(edges: readonly Edge[], others: readonly Edge[]): Edge[] {
    const out: Edge[] = [];
    for (const edge of edges) {
        const cuts: { t: number; point: PlanPoint }[] = [];
        for (const other of others) cuts.push(...splitPoints(edge.a, edge.b, other.a, other.b));
        cuts.sort((x, y) => x.t - y.t);
        let from = edge.a;
        for (const cut of cuts) {
            if (Math.hypot(cut.point.x - from.x, cut.point.y - from.y) >= MIN_PIECE) {
                out.push({ a: from, b: cut.point });
                from = cut.point;
            }
        }
        if (Math.hypot(edge.b.x - from.x, edge.b.y - from.y) >= MIN_PIECE) out.push({ a: from, b: edge.b });
    }
    return out;
}

const keyOf = (p: PlanPoint): string => `${Math.round(p.x / SNAP)}:${Math.round(p.y / SNAP)}`;

/** Links directed edges into rings, taking the sharpest left turn at a junction. */
function linkRings(pieces: readonly Edge[]): PlanPoint[][] {
    const canonical = new Map<string, PlanPoint>();
    const point = (p: PlanPoint): PlanPoint => {
        const key = keyOf(p);
        const known = canonical.get(key);
        if (known) return known;
        canonical.set(key, p);
        return p;
    };
    const outgoing = new Map<string, { from: PlanPoint; to: PlanPoint; used: boolean }[]>();
    for (const { a, b } of pieces) {
        const from = point(a);
        const to = point(b);
        if (from === to) continue;
        const list = outgoing.get(keyOf(from)) ?? [];
        list.push({ from, to, used: false });
        outgoing.set(keyOf(from), list);
    }
    const rings: PlanPoint[][] = [];
    for (const list of outgoing.values()) {
        for (const start of list) {
            if (start.used) continue;
            const ring: PlanPoint[] = [start.from];
            let current = start;
            current.used = true;
            let closed = false;
            for (let guard = 0; guard < 100000; guard += 1) {
                if (current.to === start.from) {
                    closed = true;
                    break;
                }
                ring.push(current.to);
                const options = (outgoing.get(keyOf(current.to)) ?? []).filter((edge) => !edge.used);
                if (options.length === 0) break;
                const heading = Math.atan2(current.to.y - current.from.y, current.to.x - current.from.x);
                let best = options[0];
                let bestTurn = -Infinity;
                for (const option of options) {
                    const angle = Math.atan2(option.to.y - option.from.y, option.to.x - option.from.x);
                    let turn = angle - heading;
                    while (turn <= -Math.PI) turn += Math.PI * 2;
                    while (turn > Math.PI) turn -= Math.PI * 2;
                    if (turn > bestTurn) {
                        bestTurn = turn;
                        best = option;
                    }
                }
                best.used = true;
                current = best;
            }
            if (closed && ring.length >= 3 && Math.abs(area(ring)) > MIN_PIECE) rings.push(ring);
        }
    }
    return rings;
}

const midpoint = (e: Edge): PlanPoint => ({ x: (e.a.x + e.b.x) / 2, y: (e.a.y + e.b.y) / 2 });

/**
 * The parts of `subject` that lie inside `clip`, as counter-clockwise rings (0, 1 or many). Both polygons may be
 * concave; either orientation is accepted. Edges shared with the clip polygon are kept once.
 */
export function intersectPolygons(subject: readonly PlanPoint[], clip: readonly PlanPoint[]): PlanPoint[][] {
    if (subject.length < 3 || clip.length < 3) return [];
    const a = counterClockwise(subject);
    const b = counterClockwise(clip);
    const aEdges = edgesOfRing(a);
    const bEdges = edgesOfRing(b);
    const aPieces = splitRing(aEdges, bEdges);
    const bPieces = splitRing(bEdges, aEdges);

    const kept: Edge[] = [];
    for (const piece of aPieces) {
        const where = locate(b, bEdges, midpoint(piece));
        if (where.where === 'in') kept.push(piece);
        else if (where.where === 'on' && where.edge) {
            const same =
                (piece.b.x - piece.a.x) * (where.edge.b.x - where.edge.a.x) +
                    (piece.b.y - piece.a.y) * (where.edge.b.y - where.edge.a.y) >
                0;
            if (same) kept.push(piece);
        }
    }
    for (const piece of bPieces) {
        if (locate(a, aEdges, midpoint(piece)).where === 'in') kept.push(piece);
    }
    return linkRings(kept);
}

/**
 * The parts of an open polyline that lie inside `polygon` (its boundary counts as inside), as runs of points.
 * Consecutive kept pieces are joined into one run.
 */
export function clipPolylineInside(line: readonly PlanPoint[], polygon: readonly PlanPoint[]): PlanPoint[][] {
    if (line.length < 2 || polygon.length < 3) return [];
    const ring = counterClockwise(polygon);
    const ringEdges = edgesOfRing(ring);
    const runs: PlanPoint[][] = [];
    let current: PlanPoint[] = [];
    const flush = () => {
        if (current.length >= 2) runs.push(current);
        current = [];
    };
    for (let i = 0; i + 1 < line.length; i += 1) {
        const segment: Edge = { a: line[i], b: line[i + 1] };
        for (const piece of splitRing([segment], ringEdges)) {
            const where = locate(ring, ringEdges, midpoint(piece)).where;
            if (where === 'out') {
                flush();
                continue;
            }
            const last = current[current.length - 1];
            if (!last || Math.hypot(last.x - piece.a.x, last.y - piece.a.y) > SNAP) {
                flush();
                current.push({ x: piece.a.x, y: piece.a.y });
            }
            current.push({ x: piece.b.x, y: piece.b.y });
        }
    }
    flush();
    return runs;
}

/** Signed area of a ring in mm2 (positive = counter-clockwise). */
export function ringArea(ring: readonly PlanPoint[]): number {
    return area(ring);
}
